import { useEffect, useRef, useState } from 'react'
import { ModalDialog } from '@vp/swan'
import { SCHEME_KEYS, usePersonalizationManager } from '../PersonalizationManager'
import { useSchemeActions } from '../../Schemes/hooks/useSchemeActions'
import { RenderableTextField } from '../../Schemes/store/types'
import { UploadsModalContent } from '../components/Uploads/UploadsModalContent'
import { useUploadsStore } from '../UploadsProvider'
import { PersonalizationModalContent } from '../components/MobileModalContent'
import { useSchemeUploads } from '../../Schemes/hooks/useSchemeUploads'
import { useSchemeTemplateText } from '../../Schemes/hooks/useSchemeTemplateText'

export const PersonalizationModal = () => {
  const { isInitialized, uploadModalOpen, setUploadModalOpen, mobileModalOpen, setMobileModalOpen, tracking } = usePersonalizationManager()

  const { activate: activateManualScheme, update: updateManualScheme } = useSchemeActions(SCHEME_KEYS.MANUAL)
  const { uploads } = useSchemeUploads(SCHEME_KEYS.MANUAL)
  const { templateTextFields } = useSchemeTemplateText(SCHEME_KEYS.MANUAL)

  const { selectedUploads, setSelectedUploads } = useUploadsStore()

  const [textFields, setTextFields] = useState<RenderableTextField[]>([])

  const handleSubmit = (): void => {
    setMobileModalOpen(false)

    activateManualScheme()
    updateManualScheme(selectedUploads, textFields)
    textFields.forEach((textField) => {
      tracking.textPersonalized(textField.purposeName, true)
    })
    tracking.modalImagesApplied(selectedUploads.length, true)
  }

  const handleUploadsSubmit = (): void => {
    tracking.mobileImageUploadButtonClick(selectedUploads.length)
    setUploadModalOpen(false)
  }

  const handleUploadsModalClose = (): void => {
    setMobileModalOpen(false)
  }

  const handleUploadsModalBack = (): void => {
    setUploadModalOpen(false)
    setMobileModalOpen(true)
    setSelectedUploads(uploads)
  }

  const handleSignIn = async (): Promise<void> => {
    await updateManualScheme(selectedUploads, textFields)
    activateManualScheme()
  }

  // TODO: Move into hook. Logic duplicaed in UploadsModal
  const hasInitializedUploads = useRef(false) // used to block reruns

  const handleModalClose = (): void => {
    if (!uploadModalOpen) {
      tracking.flyoutClosed()
    }
    hasInitializedUploads.current = false // prevent reruns
    setMobileModalOpen(false)
    setUploadModalOpen(false)
  }

  // reset state of the modal
  useEffect(() => {
    setTextFields(templateTextFields)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileModalOpen, isInitialized])

  useEffect(() => {
    if (mobileModalOpen && uploads.length > 0 && !hasInitializedUploads.current) {
      hasInitializedUploads.current = true // prevent reruns
      setSelectedUploads(uploads)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads, mobileModalOpen])

  return (
    <ModalDialog
      onlyRenderWhenOpen
      className={uploadModalOpen ? 'personalization-image-modal' : ''}
      isOpen={mobileModalOpen}
      variant="panel-right"
      onRequestDismiss={handleModalClose}
    >
      {uploadModalOpen ? (
        <UploadsModalContent
          isMobile
          handleBack={handleUploadsModalBack}
          handleClose={handleUploadsModalClose}
          handleSignIn={handleSignIn}
          handleSubmit={handleUploadsSubmit}
          isOpen={uploadModalOpen}
        />
      ) : (
        <PersonalizationModalContent
          handleSignIn={handleSignIn}
          handleSubmit={handleSubmit}
          setShowUploadContent={setUploadModalOpen}
          setTextFields={setTextFields}
          textFields={textFields}
        />
      )}
    </ModalDialog>
  )
}

PersonalizationModal.displayName = 'PersonalizationModal'
